import type { MarketLevelResult } from "@shared/labs/candidate-assessment";
import {
  type AxisScore,
  isInterviewDecision,
  type ThreeAxisRecommendation,
} from "@shared/labs/three-axis";
import { formatScoresAsDots } from "./format-scores";
import { MarketLevelSection } from "./market-level-section";

const AXIS_LABEL: Record<AxisScore["id"], string> = {
  gca: "GCA",
  compWay: "Comp Way",
  skills: "Skills",
};

function formatValue(a: AxisScore): string {
  return a.id === "skills" ? `L${a.value}` : a.value.toFixed(2);
}

/**
 * Hero — Stripe-flavored verdict + stat strip.
 *
 * Top zone: kicker → candidate name → summary pills/facts on the left; verdict
 * (Word-Carries-State color) on the right separated by a hairline.
 * Bottom zone: 4-stat grid (GCA / Comp Way / Skills / Faixa mercado) with
 * hairline dividers built via grid gap on card-line background.
 *
 * All existing testids, anchor jumps (#axis-*), LinkedIn link, decision
 * badges and MarketLevelSection compute behavior are preserved.
 */
export function Hero({
  rec,
  name,
  discipline,
  linkedinUrl,
  marketLevel,
  archivedEvaluationId,
  onMarketLevelComputed,
}: {
  rec: ThreeAxisRecommendation;
  name: string;
  discipline?: string;
  linkedinUrl?: string;
  marketLevel?: MarketLevelResult;
  archivedEvaluationId?: string;
  onMarketLevelComputed?: (r: MarketLevelResult) => void;
}) {
  const isImbalance = !!rec.marketImbalance;
  const isException = !isImbalance && !!rec.exceptionalAxis;
  const isBorderline = !isImbalance && !!rec.borderlineAxis;
  const interview = isInterviewDecision(rec.decision);

  const verdictWord = interview ? "Entrevistar" : "Não entrevistar";
  let verdictTone: "forest" | "muted" | "coral" | "amber" = interview ? "forest" : "muted";
  let verdictModifier: string | null = null;
  if (isImbalance && rec.marketImbalance) {
    verdictTone = "muted";
    verdictModifier = `desbalanceado · mercado ${rec.marketImbalance.marketCglDisplayName} vs skills ${rec.marketImbalance.skillsCglDisplayName}`;
  } else if (isException) {
    verdictTone = "coral";
    verdictModifier = "via excepcionalidade";
  } else if (isBorderline) {
    verdictTone = "amber";
    verdictModifier = "borderline · revisar";
  }

  // Summary pill — overall stance shorthand.
  const summaryPillTone: "forest" | "amber" | "coral" | "muted" = interview
    ? isBorderline
      ? "amber"
      : isException
        ? "coral"
        : "forest"
    : "muted";
  const summaryPillLabel = interview
    ? isBorderline
      ? "Borderline"
      : isException
        ? "Excepcionalidade"
        : "Acima do corte"
    : "Abaixo do corte";

  // Build summary fact line from axes.
  const factLine = rec.axes.map((a) => `${AXIS_LABEL[a.id]} ${formatValue(a)}`).join(" · ");

  return (
    <header className="hero">
      <div className="hero-top">
        <div className="hero-top-left">
          <div className="hero-kicker">
            Inteligência de candidatos
            {discipline ? ` · ${discipline}` : null}
          </div>
          <h1 className="hero-headline-text">{name}</h1>
          <div className="hero-summary">
            <span className={`pill ${summaryPillTone}`}>{summaryPillLabel}</span>
            <span className="dot">·</span>
            <span>{factLine}</span>
            {linkedinUrl && (
              <>
                <span className="dot">·</span>
                <a
                  href={linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="linkedin-link"
                  data-testid="hero-linkedin-link"
                >
                  LinkedIn
                </a>
              </>
            )}
          </div>
        </div>
        <div className="hero-top-right hero-decision" data-tone={verdictTone}>
          <span className="decision-label">Decisão automática</span>
          <span className={`decision-verdict tone-${verdictTone}`}>{verdictWord}</span>
          {isImbalance && rec.marketImbalance && (
            <span className="decision-imbalance-badge" data-testid="decision-imbalance-badge">
              {verdictModifier}
            </span>
          )}
          {isException && (
            <span className="decision-exception-badge" data-testid="decision-exception-badge">
              via excepcionalidade
            </span>
          )}
          {isBorderline && (
            <span className="decision-borderline-badge" data-testid="decision-borderline-badge">
              borderline · revisar
            </span>
          )}
        </div>
      </div>
      <p className="decision-reason" data-testid="decision-reason">
        {formatScoresAsDots(rec.decisionReason)}
      </p>
      <div className="stats">
        {rec.axes.map((a) => (
          <AxisStat key={a.id} axis={a} />
        ))}
        <MarketStat
          marketLevel={marketLevel}
          archivedEvaluationId={archivedEvaluationId}
          onMarketLevelComputed={onMarketLevelComputed}
        />
      </div>
    </header>
  );
}

function AxisStat({ axis }: { axis: AxisScore }) {
  const isSkills = axis.id === "skills";
  const barMax = isSkills ? 5 : 1;
  const fillPct = Math.min((axis.value / barMax) * 100, 100);
  const markerPct = (axis.threshold / barMax) * 100;
  const fail = !isSkills && !axis.pass;
  const margin = axis.margin;
  const marginSign = margin >= 0 ? "+" : "";
  const marginText = isSkills
    ? `vs L${axis.threshold} esperado`
    : `vs corte ${axis.threshold.toFixed(2)}`;
  return (
    <a className="stat" href={`#axis-${axis.id}`} data-testid={`axis-stat-${axis.id}`}>
      <div className="label">{AXIS_LABEL[axis.id]}</div>
      <div className={`value ${fail ? "fail" : ""}`}>{formatValue(axis)}</div>
      <div className="delta">
        <span className={margin >= 0 ? "up" : "down"}>
          {isSkills
            ? margin === 0
              ? "no nível"
              : `${marginSign}${margin.toFixed(0)} nível`
            : `${marginSign}${margin.toFixed(2)}`}
        </span>
        <span>{marginText}</span>
        {axis.closeCall && !isSkills && (
          <a className="close-ribbon score-jump" href={`#sens-${axis.id}`}>
            borderline
          </a>
        )}
      </div>
      <div className="spark">
        <div className={`fill ${fail ? "fail" : ""}`} style={{ width: `${fillPct}%` }} />
        {!isSkills && <div className="marker" style={{ left: `${markerPct}%` }} />}
      </div>
    </a>
  );
}

function MarketStat({
  marketLevel,
  archivedEvaluationId,
  onMarketLevelComputed,
}: {
  marketLevel?: MarketLevelResult;
  archivedEvaluationId?: string;
  onMarketLevelComputed?: (r: MarketLevelResult) => void;
}) {
  // Keep the existing dropdown UX inside the stat cell — preserves
  // recompute + reasoning + confidence behavior. The label/value above is
  // a static summary; clicking expands the dropdown beneath.
  return (
    <div className="stat" data-testid="market-stat">
      <div className="label">Faixa mercado</div>
      <div className="value" style={{ fontSize: 18 }}>
        {marketLevel ? marketLevel.cglDisplayName : "—"}
      </div>
      <div className="delta">
        <span>{marketLevel ? marketLevel.marketLevel : "não computado"}</span>
      </div>
      <MarketLevelSection
        marketLevel={marketLevel}
        archivedEvaluationId={archivedEvaluationId}
        onMarketLevelComputed={onMarketLevelComputed}
      />
    </div>
  );
}
